import { Category, type DataPoint, type ProviderFile, toDataPoint } from "fileParsing/types";
import { genericPath } from "fileParsing/wrangler/types";
import { stringify } from "@utils/array";
import type { MappingJSON, ParserJSON } from "containers/Visuals/Sankey/policyParserTypes";
import { mapDataPointToPolicyAttributes } from "@/containers/Visuals/Sankey/parserProcessing";
import { isValidDate, processTime } from "./date";
import { extractDate } from "./extractTemplates";
import { type DataTemplate, type Path, type StructureType, Visibility, WILDCARD, type WranglerItem } from "./types";

const EPOCH_CUTOFF = new Date("2004-01-01").getTime();

/**
 * Data points whose timestamp could not be parsed fall back to 1970.
 * Replace those with the earliest date in the whole export.
 */
function fillEpochDates(files: ProviderFile[]): ProviderFile[] {
  const earliestDate = files.reduce(
    (min, file) =>
      file.data.reduce(
        (m, { date }) => (date.getTime() > EPOCH_CUTOFF && date.getTime() < m ? date.getTime() : m),
        min,
      ),
    Infinity,
  );
  console.log("fillEpochDates ", earliestDate, files.length);

  if (earliestDate === Infinity) return files;
  const newDate = new Date(earliestDate);

  return files.map((file) => ({
    ...file,
    data: file.data.map((point) => (point.date.getTime() > EPOCH_CUTOFF ? point : { ...point, date: newDate })),
  }));
}

const compileFn = (
  wranglerItem: WranglerItem,
  file: ProviderFile,
  deserializedOutputs?: Record<string, [MappingJSON, ParserJSON]>,
): ProviderFile => {
  // Item described by path must be in basePath.
  const isLocalPath = (basePath: Path, path: Path): boolean =>
    path.length > basePath.length && basePath.every((p, ix) => p === path[ix]);

  // Path must be local to basePath and cannot be in an array below basePath.
  // Thus, each object in basePath has at most one item at path.
  const strictLocalPath = (basePath: Path, path: Path): boolean =>
    isLocalPath(basePath, path) && path.slice(basePath.length + 1).every((p) => p !== WILDCARD);

  // Returns a path relative to basePath.
  // If path is not local to basePath, returns an absolute path using
  // the array indices resolved from basePath.
  const toLocalPath = (basePath: Path, path: Path): Path => {
    if (isLocalPath(basePath.map(genericPath), path)) {
      return path.slice(basePath.length);
    }

    return path.reduce(
      ({ replace, path: resolvedPath }, segment, index) => {
        let shouldReplace = replace;

        if (replace && basePath[index] !== undefined) {
          if (segment === null || segment === basePath[index]) {
            return {
              replace,
              path: [...resolvedPath, basePath[index]],
            };
          }

          shouldReplace = false;
        }

        return {
          replace: shouldReplace,
          path: [...resolvedPath, segment],
        };
      },
      { replace: true, path: [] as Path },
    ).path;
  };

  const data =
    wranglerItem.templates
      .filter((template) => template.visibility !== Visibility.hidden)
      .reduce<DataPoint[]>(
        (compiledData, currentTemplate) => [
          ...compiledData,
          ...extractDataPoints(file.parserData.structure, currentTemplate.path, {
            exactPath: [],
            additional: {},
          })
            .map(({ content: structure, exactPath }) => {
              const genericExactPath = exactPath.map(genericPath);

              // STEP: TIMESTAMP
              for (const candidate of currentTemplate.selectedTimestamps) {
                if (!strictLocalPath(genericExactPath, candidate.path)) {
                  continue;
                }

                const localPath = toLocalPath(exactPath, candidate.path);
                const extracted = extractDate(structure, localPath);

                for (const value of extracted) {
                  if (typeof value === "number" || typeof value === "string") {
                    const date = processTime(value, candidate.arguments);

                    if (isValidDate(date)) {
                      return { structure, date, exactPath };
                    }
                  }
                }
              }

              return {
                structure,
                date: new Date(0),
                exactPath,
              };
            })
            .map(({ structure, exactPath, date }) => {
              const genericExactPath = exactPath.map(genericPath);

              const transformfn = (path: Path) => {
                if (isLocalPath(genericExactPath, path)) {
                  return {
                    local: true,
                    path: toLocalPath(exactPath, path),
                  };
                }

                return {
                  local: false,
                  path: toLocalPath(exactPath, path),
                };
              };

              return {
                structure,
                date,
                exactPath,

                // STEP: URI
                uri: extractData(currentTemplate.selectedUris, structure, file.parserData.structure, transformfn)?.[0],

                // STEP: CONTACTS
                contacts: extractData(
                  currentTemplate.selectedContacts,
                  structure,
                  file.parserData.structure,
                  transformfn,
                ),

                // STEP: LATITUDES
                latitudes: extractData(
                  currentTemplate.selectedLatitudes,
                  structure,
                  file.parserData.structure,
                  transformfn,
                )?.[0],

                // STEP: LONGITUDES
                longitudes: extractData(
                  currentTemplate.selectedLongitudes,
                  structure,
                  file.parserData.structure,
                  transformfn,
                )?.[0],
              };
            })
            .map(({ structure, date, uri, exactPath, contacts, latitudes, longitudes }) => {
              const blockList = ([] as Path[])
                .concat(
                  currentTemplate.selectedUris,
                  currentTemplate.selectedTimestamps.map(({ path }) => path),
                  currentTemplate.selectedLatitudes,
                  currentTemplate.selectedLongitudes,
                )
                .map((path) => path.map(genericPath));

              // STEP: TEXT AND SUBCATEGORY
              if (!file.parserData?.structure) {
                throw new Error("filter.parserData.structure missing!");
              }

              const text = compileText(
                currentTemplate.content,
                (path) => {
                  const local = isLocalPath(exactPath.map(genericPath), path);

                  return {
                    local,
                    path: toLocalPath(exactPath, path),
                  };
                },
                structure,
                file.parserData.structure,
                blockList,
              );

              const subcategory = transformString(
                compileText(
                  currentTemplate.subcategory,
                  (path) => {
                    const local = isLocalPath(exactPath.map(genericPath), path);

                    return {
                      local,
                      path: toLocalPath(exactPath, path),
                    };
                  },
                  structure,
                  file.parserData.structure,
                  blockList,
                ),
              );

              // STEP: COMPILE DATAPOINT
              return toDataPoint(
                date,
                text,
                currentTemplate.category ?? Category.Other,
                subcategory,
                uri ? String(uri) : undefined,
                Array.isArray(contacts)
                  ? (contacts as string[])
                  : typeof contacts === "string"
                    ? [String(contacts)]
                    : undefined,
                !Number.isNaN(Number(longitudes)) && !Number.isNaN(Number(latitudes))
                  ? [
                      Math.abs(Number(longitudes) + 180) > 360 ? Number(longitudes) / 10000000 : Number(longitudes),
                      Math.abs(Number(latitudes) + 90) > 180 ? Number(latitudes) / 10000000 : Number(latitudes),
                    ]
                  : undefined,
              );
            }),
        ],
        [],
      ) ?? [];

  // STEP: POLICY ATTRIBUTES
  let extendedData = data;

  if (file.providerId !== "wrangled") {
    const parserOutput = deserializedOutputs?.[file.providerId];

    if (parserOutput?.[0] && parserOutput[1]) {
      extendedData = data.map((dataPoint) =>
        mapDataPointToPolicyAttributes(dataPoint, file, parserOutput[0], parserOutput[1]),
      );
    }
  }

  return {
    ...file,
    dataCategory: mostFrequentCategory(data) ?? Category.Other,
    data: extendedData,
  };
};

/**
 * Compile values of a DataTemplate array into an output string.
 *
 * @param arr Data templates to compile.
 * @param transformPath Transforms global paths into paths relative to the
 * data point and identifies whether the path is local.
 * @param dataPoint Raw value of the data point.
 * @param fileStructure Raw value of the complete file.
 * @param blockList Generic paths to omit when compiling an entire object.
 */
function compileText(
  arr: DataTemplate[],
  transformPath: (pointPath: Path) => {
    local: boolean;
    path: Path;
  },
  dataPoint: unknown,
  fileStructure: StructureType,
  blockList: Path[] = [],
): string {
  return arr
    .map((template) => {
      if (template.type === "text") {
        return template.value;
      }

      if (template.type === "data") {
        const { local, path } = transformPath(template.value);
        const values = extractDate(local ? dataPoint : fileStructure, path);

        if (values.length <= 1) {
          return stringify(applyBlockList(values[0], blockList, template.value.map(genericPath)));
        }

        return `${stringify(
          applyBlockList(values[0], blockList, template.value.map(genericPath)),
        )}, +${values.length - 1} more.`;
      }

      throw new Error("Unknown data type found in compileText step.");
    })
    .join("");
}

function applyBlockList(value: unknown, blockList: Path[], basePath: Path = []): unknown {
  if (blockList.length === 0) return value;

  if (Array.isArray(value)) {
    if (value.length > 0) {
      return [applyBlockList(value[0], blockList, basePath.concat([WILDCARD]))];
    }

    return value;
  }

  if (typeof value === "object" && value !== null) {
    const data = Object.entries(value)
      .filter(([key]) => {
        const currentPath = basePath.concat([key]).toString();

        return blockList.every((path) => path.toString() !== currentPath);
      })
      .map(([key, nestedValue]) => [key, applyBlockList(nestedValue, blockList, basePath.concat([key]))]);

    return Object.fromEntries(data);
  }

  return value;
}

function extractData(
  paths: Path[],
  dataPoint: unknown,
  fileStructure: StructureType,
  transformPath: (path: Path) => {
    local: boolean;
    path: Path;
  },
): unknown[] {
  for (const candidate of paths) {
    const { local, path } = transformPath(candidate);
    const extracted = extractDate(local ? dataPoint : fileStructure, path);

    if (extracted.length > 0) {
      return extracted;
    }
  }

  return [];
}

const extractDataPoints = (
  tree: unknown,
  path: Path,
  data: {
    exactPath: Path;
    additional: unknown;
  },
): {
  content: unknown;
  exactPath: Path;
}[] => {
  if (path.length === 0 && Array.isArray(tree)) {
    return tree.map((element, index) => ({
      content: element,
      exactPath: [...data.exactPath, index],
    }));
  }

  if (path[0] === null && Array.isArray(tree)) {
    return tree
      .map((element, index) =>
        extractDataPoints(element, path.slice(1), {
          exactPath: [...data.exactPath, index],
          additional: data.additional,
        }),
      )
      .filter((items) => items !== undefined)
      .reduce((result, items) => [...result, ...items], []);
  }

  if (typeof tree === "object" && tree !== null) {
    const record = tree as Record<string, unknown>;

    if (path[0] === null) {
      return Object.values(record)
        .map((value, index) =>
          extractDataPoints(value, path.slice(1), {
            exactPath: [...data.exactPath, index],
            additional: data.additional,
          }),
        )
        .filter((items) => items !== undefined)
        .reduce((result, items) => [...result, ...items], []);
    }

    if (path[0] in record && record[path[0]] !== undefined) {
      const { [path[0]]: pathValue } = record;

      return extractDataPoints(pathValue, path.slice(1), {
        exactPath: [...data.exactPath, path[0]],
        additional: data.additional,
      });
    }
  }

  return [];
};

/**
 * Transforms the input string by uppercasing the first letter of the string
 * and any letter following a space, underscore, or hyphen. Underscores are
 * replaced with spaces while hyphens are preserved.
 */
function transformString(value: string): string {
  return value.replaceAll(/(^\w|\s\w|_\w|-\w)/g, (match) =>
    match.length === 1 || match[0] === "-" ? match.toUpperCase() : ` ${match[1].toUpperCase()}`,
  );
}

function mostFrequentCategory(data: DataPoint[]): Category {
  const map = data.reduce(
    (acc, point) => {
      const count = (acc[point.category] || 0) + 1;
      return Object.apply(acc, { [point.category]: count });
    },
    {} as Record<Category, number>,
  );
  return Object.keys(map).reduce((a, b) => (map[a] > map[b] ? a : b), Object.keys(map)[0]) as Category;
}

export { compileFn, compileText, fillEpochDates };
